import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { vi } from "date-fns/locale";
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { selectAddresses } from '@/modules/addresses/redux/AddressRedux';

// Components
import OrderDetailTabs from "./OrderDetailTabs";
import OrderActions from "./OrderActions";
import OrderPrintTemplateRuntime from "./OrderPrintTemplateRuntime";
import MessageSnackbar from "@/__core/helpers/components/MessageSnackbar";
import LoadingBackdrop from "@/__core/helpers/components/LoadingBackdrop";
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

// Hooks and utilities
import { useOrderForm } from "./NewOrderForm";
import { validateOrderForm, prepareOrderData } from "./orderDetails/OrderValidation";
import { handleBackendError } from "./OrderErrorHandler";
import { isBackendErrorResponse } from "@/__core/helpers/Error";
import { OrderStatusProvider } from "../context/OrderStatusContext";

// API functions
import { createOrder } from "../redux/OrderCRUD";
import { createAddress, getAddresses } from "@/modules/addresses/redux/AddressCRUD";
import { RelatedEntityTabRecord } from "@/__core/tabs";
import { MaterialLine } from "./orderDetails/OrderMaterials";

// Function to find or create address from string
const findOrCreateAddress = async (addressString: string) => {
  try {
    // First, try to find existing address
    const addressesResponse = await getAddresses();
    const addresses = addressesResponse.data || [];

    // Look for address with matching full_address
    const existingAddress = addresses.find(
      (addr: any) =>
        addr.full_address
          ?.toLowerCase()
          .includes(addressString.toLowerCase()) ||
        addr.address_line?.toLowerCase().includes(addressString.toLowerCase())
    );

    if (existingAddress) {
      return existingAddress.id;
    }

    // If not found, create new address
    // For simplicity, we'll create a basic address structure
    const addressData = {
      address_line: addressString,
      ward: "", // We'll need to parse this from addressString
      province: "", // We'll need to parse this from addressString
      postal_code: "",
    };

    const newAddressResponse = await createAddress(addressData);
    return newAddressResponse.data.id;
  } catch (error) {
    return "";
  }
};

// Function to create address from object
const createAddressFromObject = async (addressObj: any) => {
  try {
    if (!addressObj || !addressObj.address_line) {
      return null;
    }

    // First, try to find existing address with same address_line
    const addressesResponse = await getAddresses();
    const addresses = addressesResponse.data || [];

    const existingAddress = addresses.find(
      (addr: any) => addr.address_line === addressObj.address_line
    );

    if (existingAddress) {
      return existingAddress.id;
    }

    // Create new address from object
    // Only include fields that have values to avoid blank validation errors
    const addressData: any = {
      address_line: addressObj.address_line || "",
    };
    
    // Only add optional fields if they have values
    if (addressObj.ward && addressObj.ward.trim() !== '') {
      addressData.ward = addressObj.ward;
    }
    if (addressObj.province && addressObj.province.trim() !== '') {
      addressData.province = addressObj.province;
    }
    if (addressObj.postal_code && addressObj.postal_code.trim() !== '') {
      addressData.postal_code = addressObj.postal_code;
    }

    const newAddressResponse = await createAddress(addressData);
    return newAddressResponse.data.id;
  } catch (error) {
    return null;
  }
};


interface NewOrderProps {
  onSave?: (orderData: any) => Promise<any>;
  onUpdate?: (orderData: any, orderId?: string) => Promise<any>;
  onCancel?: () => void;
  loading?: boolean;
  error?: string | null;
  order?: any; // For edit mode
  isEditMode?: boolean;
  readOnly?: boolean;
  onUnsavedChanges?: (hasChanges: boolean, tabId?: number) => void;
  isActive?: boolean; // Whether this tab is currently active/visible
  tabId?: number; // Tab ID for tracking changes per tab
  contextCompanyId?: string; // Company selected from toolbar filter
  canRead?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canPrint?: boolean;
  canExport?: boolean;
  onOpenIssue?: (record: RelatedEntityTabRecord<MaterialLine>) => void;
}

const NewOrder = React.memo(function NewOrder({
  onSave,
  onUpdate,
  onCancel,
  loading = false,
  error,
  order,
  isEditMode: initialEditMode = false,
  readOnly = false,
  onUnsavedChanges,
  isActive = true,
  tabId,
  contextCompanyId,
  canRead: canReadProp = false,
  canCreate: canCreateProp = false,
  canUpdate: canUpdateProp = false,
  canDelete: canDeleteProp = false,
  canPrint = false,
  canExport = false,
  onOpenIssue,
}: NewOrderProps) {
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "error" | "success" | "warning" | "info"
  >("error");
  
  // Track date changes from OrderProgress tab
  const [hasDateChanges, setHasDateChanges] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState<string>('info');
  const saveDatesRef = React.useRef<(() => Promise<void>) | null>(null);

  // Print functionality
  const printRef = useRef<HTMLDivElement>(null);
  const [printType, setPrintType] = useState<string>('order');
  const [printKey, setPrintKey] = useState(0);
  const [printLoading, setPrintLoading] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [createdOrder, setCreatedOrder] = useState<any>(null);

  // Get addresses from Redux store
  const addresses = useSelector(selectAddresses);
  const orderTransactions = useSelector((state: any) => state.orders?.orderTransactions || []);
  
  // Ensure addresses is always an array
  const safeAddresses = Array.isArray(addresses) ? addresses : [];

  const canEdit = useMemo(() => {
    if (readOnly) {
      return false;
    }

    if (isEditMode) {
      return canUpdateProp === true;
    }
    return canCreateProp === true;
  }, [isEditMode, canCreateProp, canUpdateProp, readOnly]);

  // Users who can view an order can also view its read-only summary.
  const canViewOrderSummary = canReadProp === true || canEdit;

  // Use the custom hook for form management
  const {
    formData,
    orderItems,
    setFormData,
    setOrderItems,
    customers,
    employees,
    units,
    salesChannels,
    wallets,
    companies,
    orderStatuses,
    additionalFees,
    handleInputChange,
    handleItemChange,
    addOrderItem,
    addGroupItem,
    removeOrderItem,
    resetForm,
    resetChangeTracking,
    handleOrderStatusCreated,
    refreshOrderStatuses,
    handleSalesChannelCreated,
    handleWalletCreated,
    productTypes,
    totalAmount,
    grandTotal,
    hasChanges,
    initialFormDataLoaded,
  } = useOrderForm({ order, isEditMode });

  // Sync isEditMode with prop when it changes externally (from tab.type switch)
  // Only sync when we have createdOrder (to avoid resetting during order creation)
  React.useEffect(() => {
    if (createdOrder && initialEditMode !== isEditMode) {
      setIsEditMode(initialEditMode);
    }
  }, [initialEditMode, createdOrder, isEditMode]);

  // Prevent stale date-change flag from previous order/session affecting Update button state.
  React.useEffect(() => {
    setHasDateChanges(false);
  }, [order?.id, isEditMode]);

  const hasUnsavedChanges = hasChanges || hasDateChanges;
  const updateButtonEnabledChanges =
    activeDetailTab === 'progress' ? hasUnsavedChanges : hasChanges;
  const shouldShowActions = activeDetailTab === 'info' || (activeDetailTab === 'progress' && hasDateChanges);

  // Notify parent component about unsaved changes
  // Use the SAME logic as Update button: only notify when active
  // This ensures consistency between button state and warning dialog
  React.useEffect(() => {
    if (onUnsavedChanges && initialFormDataLoaded && isActive) {
      onUnsavedChanges(hasUnsavedChanges, tabId);
    }
  }, [hasUnsavedChanges, onUnsavedChanges, initialFormDataLoaded, isActive, tabId]);

  // Track data loading state with timeout to prevent infinite loading
  useEffect(() => {
    if (isEditMode && order) {
      // Check if we're waiting for essential data to load
      const isWaitingForData = customers.length === 0 || 
                              salesChannels.length === 0 || 
                              orderStatuses.length === 0;
      
      // Also check if order form data hasn't been loaded yet
      // But add a reasonable timeout - don't wait forever if something goes wrong
      const isFormDataNotLoaded = !initialFormDataLoaded;
      
      setIsDataLoading(isWaitingForData || isFormDataNotLoaded);
      
      // Add timeout to force loading off after 5 seconds
      // This prevents infinite loading if something goes wrong
      const timeout = setTimeout(() => {
        setIsDataLoading(false);
      }, 5000);
      
      return () => clearTimeout(timeout);
    } else {
      setIsDataLoading(false);
    }
  }, [isEditMode, order, customers.length, salesChannels.length, orderStatuses.length, initialFormDataLoaded]);

  // Function to show snackbar
  const showSnackbar = (
    message: string,
    severity: "error" | "success" | "warning" | "info" = "error"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Function to close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Enhanced input change handler with auto-fill logic
  const handleEnhancedInputChange = (field: string, value: any) => {
    handleInputChange(field, value);

    // Auto-fill delivery info when customer is selected
    if (field === "customer_id" && value) {
      const selectedCustomer = customers.find((c: any) => c.id === value);
      
      if (selectedCustomer) {
        const phone = selectedCustomer.phone || selectedCustomer.contact_phone || "";
        const addressString = selectedCustomer.address || selectedCustomer.full_address || "";

        handleInputChange("delivery_phone", phone);

        // Auto-fill address if available
        if (addressString) {
          findOrCreateAddress(addressString).then((addressId) => {
            if (addressId) {
              handleInputChange("delivery_address_obj", addressId);
            }
          });
        }
      }
    }
  };

  // Function to handle save
  const handleSave = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Validate form
      const validationErrors = validateOrderForm(formData, orderItems);
      if (validationErrors.length > 0) {
        showSnackbar(validationErrors[0].message);
        return;
      }

      // Create address if delivery_address_obj is an object
      let finalDeliveryAddressObj = formData.delivery_address_obj;
      
      if (formData.delivery_address_obj && typeof formData.delivery_address_obj === 'object') {
        const addressId = await createAddressFromObject(formData.delivery_address_obj);
        if (addressId) {
          // Use the created address ID
          finalDeliveryAddressObj = addressId;
        }
      }

      // Prepare order data with correct delivery_address_obj
      const normalizeCompanyId = (value: any): string => {
        const normalized = typeof value === 'string' ? value.trim() : '';
        if (!normalized || normalized === 'all' || normalized === 'global') return '';
        return normalized;
      };
      const resolvedCompanyId =
        normalizeCompanyId(formData.company_id) ||
        normalizeCompanyId(contextCompanyId) ||
        normalizeCompanyId(order?.company_id) ||
        normalizeCompanyId(order?.company?.id) ||
        "";
      const orderData = prepareOrderData(
        {
          ...formData,
          company_id: resolvedCompanyId,
          delivery_address_obj: finalDeliveryAddressObj,
        },
        orderItems
      );

      // Call onSave callback if provided
      if (onSave) {
        const response = await onSave(orderData);
        const savedOrder = response?.order ?? (response?.id ? response : null);

        if (!savedOrder?.id || isBackendErrorResponse(savedOrder)) {
          const errorMessage = handleBackendError(savedOrder ?? response);
          showSnackbar(errorMessage);
          return;
        }

        // Update form with actual data from backend
        setFormData((prev) => ({
          ...prev,
          order_code: savedOrder.code,
          order_code_prefix: savedOrder.order_code_prefix || "",
          order_code_suffix: savedOrder.order_code_suffix || savedOrder.code || "",
          customer_id: savedOrder.customer?.id || savedOrder.customer_id || "",
          sales_channel_id: savedOrder.sales_channel?.id || savedOrder.sales_channel_id || "",
          sales_employee_id: savedOrder.sales_employee?.id || savedOrder.sales_employee_id || "",
          status_id: savedOrder.status?.id || savedOrder.status_id || "",
          sale_date: savedOrder.sale_date ? new Date(savedOrder.sale_date) : null,
          delivery_date: savedOrder.delivery_date ? new Date(savedOrder.delivery_date) : new Date(),
          delivery_phone: savedOrder.delivery_phone || "",
          delivery_address_obj: savedOrder.delivery_address?.id || savedOrder.delivery_address_obj || "",
          notes: savedOrder.notes || "",
        }));

        // Update orderItems with data from backend if available
        if (savedOrder.groups && savedOrder.items) {
          const newOrderItems: any[] = [];

          // Add groups first
          savedOrder.groups.forEach((group: any) => {
            newOrderItems.push({
              id: group.id,
              product_name: "",
              color: "",
              quantity: 0,
              unit: null,
              unit_id: "",
              unit_name: "",
              unit_price: 0,
              total_price: 0,
              isGroup: true,
              groupName: group.name,
            });
          });

          // Add items
          savedOrder.items.forEach((item: any) => {
            newOrderItems.push({
              id: item.id,
              product_name: item.product_name,
              color: item.color,
              quantity: item.quantity,
              unit: item.unit ? { id: String(item.unit.id || ""), name: item.unit.name || "" } : null,
              unit_id: item.unit?.id || item.unit_id || "",
              unit_name: item.unit?.name || item.unit_name || "",
              unit_price: item.unit_price,
              total_price: item.total_price,
              isGroup: false,
            });
          });

          setOrderItems(newOrderItems);
        }

        setCreatedOrder({ order: savedOrder });
        showSnackbar("Đơn hàng đã được tạo thành công!", "success");

        // Note: isEditMode will be set by OrdersModule via updateTab
      } else {
        // Fallback: create order directly if no onSave callback
        await createOrder(orderData);
        showSnackbar("Đơn hàng đã được tạo thành công!", "success");
      }
    } catch (error: any) {
      const errorMessage = handleBackendError(error);
      showSnackbar(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle update
  const handleUpdate = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Capture date-save state before any update-triggered rerender can clear refs.
      const shouldSaveDates = hasDateChanges;
      const saveDatesFn = saveDatesRef.current;

     
      const validationErrors = validateOrderForm(formData, orderItems);
      if (validationErrors.length > 0) {
        showSnackbar(validationErrors[0].message);
        return;
      }

      // Create address if delivery_address_obj is an object
      let finalDeliveryAddressObj = formData.delivery_address_obj;
      
      if (formData.delivery_address_obj && typeof formData.delivery_address_obj === 'object') {
        const addressId = await createAddressFromObject(formData.delivery_address_obj);
        if (addressId) {
          // Use the created address ID
          finalDeliveryAddressObj = addressId;
        }
      }

      // Prepare order data with correct delivery_address_obj
      const normalizeCompanyId = (value: any): string => {
        const normalized = typeof value === 'string' ? value.trim() : '';
        if (!normalized || normalized === 'all' || normalized === 'global') return '';
        return normalized;
      };
      const resolvedCompanyId =
        normalizeCompanyId(formData.company_id) ||
        normalizeCompanyId(contextCompanyId) ||
        normalizeCompanyId(order?.company_id) ||
        normalizeCompanyId(order?.company?.id) ||
        "";
      const orderData = prepareOrderData(
        {
          ...formData,
          company_id: resolvedCompanyId,
          delivery_address_obj: finalDeliveryAddressObj,
        },
        orderItems
      );

      if (onUpdate) {
        const orderId = isEditMode ? (order?.id || order?.order?.id) : createdOrder?.order?.id;
        if (orderId) {
          const updateResult = await onUpdate(orderData, orderId);
          const updatedOrder = updateResult?.data ?? updateResult;

          if (isBackendErrorResponse(updatedOrder) || (updatedOrder && !updatedOrder.id)) {
            showSnackbar(handleBackendError(updatedOrder ?? updateResult));
            return;
          }
          
          // Save dates from OrderProgress if there are changes
          if (shouldSaveDates && saveDatesFn) {
            try {
              await saveDatesFn();
              setHasDateChanges(false);
            } catch (error: any) {
              // Error already shown by OrderProgress component
              console.error('Error saving dates:', error);
            }
          }
          
          showSnackbar("Đơn hàng đã được cập nhật thành công!", "success");
          
          // Reset change tracking after successful update
          resetChangeTracking();
        } else {
          showSnackbar("Không tìm thấy ID đơn hàng để cập nhật", "error");
        }
      } else {
        showSnackbar("Hàm cập nhật không được cung cấp", "error");
      }
    } catch (error: any) {
      const errorMessage = handleBackendError(error);
      showSnackbar(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to reset to new order mode
  const handleResetToNew = () => {
    setIsEditMode(false);
    setCreatedOrder(null);
    resetForm();
    setSnackbarMessage("");
  };

  // Toolbar handlers
  const handleMailing = () => {
    showSnackbar("Chức năng Mailing chưa được implement", "info");
  };

  // Print handler
  const triggerPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Don-hang-${formData.order_code || 'new'}`,
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 10mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
        }
        .print-container {
          width: 100% !important;
          max-width: none !important;
        }
        .print-table {
          width: 100% !important;
          table-layout: fixed !important;
        }
        .print-table th,
        .print-table td {
          padding: 4px !important;
        }
        .print-table th:not(:nth-child(2)),
        .print-table td:not(:nth-child(2)) {
          white-space: nowrap !important;
        }
        .print-table th:nth-child(2),
        .print-table td:nth-child(2) {
          white-space: normal !important;
          word-wrap: break-word !important;
          line-height: 1.2 !important;
        }
        .print-table th:nth-child(1) { width: 8% !important; }
        .print-table th:nth-child(2) { width: 40% !important; }
        .print-table th:nth-child(3) { width: 10% !important; }
        .print-table th:nth-child(4) { width: 10% !important; }
        .print-table th:nth-child(5) { width: 8% !important; }
        .print-table th:nth-child(6) { width: 12% !important; }
        .print-table th:nth-child(7) { width: 12% !important; }
      }
    `,
  });

  const pendingPrintRef = useRef(false);

  const handlePrint = React.useCallback((type: string) => {
    setPrintType(type);
    setPrintKey((k) => k + 1);
    setPrintLoading(true);
    pendingPrintRef.current = true;
  }, []);

  // Called by OrderPrintTemplateRuntime when settings are loaded and rendered
  const handlePrintReady = React.useCallback(() => {
    if (!pendingPrintRef.current) return;
    pendingPrintRef.current = false;
    // Small delay to ensure DOM is painted before printing
    setTimeout(() => {
      setPrintLoading(false);
      triggerPrint();
    }, 100);
  }, [triggerPrint]);


  const handleDelete = () => {
    showSnackbar("Chức năng Xóa chưa được implement", "info");
  };


  // Handler for payment collection
  const handlePaymentCollected = async (transaction: any) => {
    
    const transactionTypeName = transaction.transaction_type_name || 'Transaction';
    const amount = new Intl.NumberFormat('vi-VN').format(transaction.actual_amount || 0);
    
    showSnackbar(
      `Thu tiền thành công! ${transactionTypeName} - ${amount} VNĐ`, 
      "success"
    );
    
    // Optionally reload order data to refresh transaction list
    // This would need to be implemented if we want to auto-refresh
  };



  return (
    <OrderStatusProvider refreshOrderStatuses={refreshOrderStatuses}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
      <Box
        sx={{
          ...sharedFormStyles.formPageContainer,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Message Snackbar */}
        <MessageSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleCloseSnackbar}
        />

        {/* Loading Backdrop */}
        <LoadingBackdrop
          open={isDataLoading || printLoading}
          message={printLoading ? "Đang tải mẫu in..." : "Đang tải dữ liệu..."}
          size={50}
        />

        {/* Order Detail Tabs with integrated Toolbar */}
        <OrderDetailTabs
          activeTab={activeDetailTab}
          onActiveTabChange={setActiveDetailTab}
          formData={formData}
          customers={customers}
          companies={companies}
          salesChannels={salesChannels}
          wallets={wallets}
          orderStatuses={orderStatuses}
          employees={employees}
          orderItems={orderItems}
          units={units}
          additionalFees={additionalFees}
          productTypes={productTypes}
          totalAmount={totalAmount}
          grandTotal={grandTotal}
          isEditMode={isEditMode}
          orderId={order?.id || createdOrder?.order?.id}
          contextCompanyId={contextCompanyId}
          disabled={!canEdit}
          canViewOrderSummary={canViewOrderSummary}
          onInputChange={(field: string, value: any) => handleEnhancedInputChange(field, value)}
          onItemChange={(index: number, field: string, value: any) => handleItemChange(index, field as any, value)}
          onAddOrderItem={addOrderItem}
          onAddGroupItem={addGroupItem}
          onRemoveOrderItem={removeOrderItem}
          onReorderItems={setOrderItems}
          onOrderStatusCreated={handleOrderStatusCreated}
          onOrderStatusUpdated={refreshOrderStatuses}
          onSalesChannelCreated={handleSalesChannelCreated}
          onWalletCreated={handleWalletCreated}
          onAdditionalFeesChange={(fees) => {
            // This would need to be implemented in the hook
          }}
          onPaymentCollected={handlePaymentCollected}
          onPrint={handlePrint}
          onDelete={handleDelete}
          onExport={handleMailing}
          canPrint={canPrint}
          canExport={canExport}
          canDelete={canDeleteProp === true}
          onDateChangesChange={setHasDateChanges}
          onSaveDatesRef={(saveFn) => { saveDatesRef.current = saveFn; }}
          onOpenIssue={onOpenIssue}
        />
        
        {/* Actions */}
        {shouldShowActions && (
          <OrderActions
            isEditMode={isEditMode}
            loading={loading}
            isSubmitting={isSubmitting}
            hasChanges={updateButtonEnabledChanges}
            disabled={!canEdit}
            onCancel={onCancel || (() => {})}
            onSave={handleSave}
            onUpdate={handleUpdate}
            onResetToNew={handleResetToNew}
          />
        )}

        {/* Hidden Print Template */}
        {printLoading && (
          <Box sx={sharedFormStyles.hiddenContainer}>
            <div ref={printRef}>
              <OrderPrintTemplateRuntime
                key={printKey}
                printType={printType}
                onReady={handlePrintReady}
                orderData={{
                  order_code: formData.order_code,
                  sale_date: formData.sale_date || undefined,
                  delivery_date: formData.delivery_date || undefined,
                  customer: {
                    name: customers.find((c: any) => c.id === formData.customer_id)?.name,
                    phone: customers.find((c: any) => c.id === formData.customer_id)?.phone,
                    address: customers.find((c: any) => c.id === formData.customer_id)?.address,
                  },
                  sales_employee: {
                    name: (() => {
                      const employee = employees.find((e: any) => e.id === formData.sales_employee_id);
                      return employee?.nickname || employee?.name || '';
                    })(),
                  },
                  company: {
                    name: companies.find((c: any) => c.id === formData.company_id)?.name,
                    code: companies.find((c: any) => c.id === formData.company_id)?.code,
                    company_type_display: companies.find((c: any) => c.id === formData.company_id)?.company_type_display,
                    logo: companies.find((c: any) => c.id === formData.company_id)?.logo || undefined,
                    address: (() => {
                      const company = companies.find((c: any) => c.id === formData.company_id);
                      if (!company) return undefined;

                      if (!company.address) return undefined;

                      if (typeof company.address === 'string') {
                        return company.address;
                      }

                      const addr = company.address as any;
                      if (addr.full_address) return addr.full_address;

                      const parts = [
                        addr.address_line,
                        addr.ward_name || addr.ward,
                        addr.district,
                        addr.province_name || addr.province,
                      ].filter(Boolean);
                      return parts.length > 0 ? parts.join(', ') : undefined;
                    })(),
                    phone: companies.find((c: any) => c.id === formData.company_id)?.phone || undefined,
                    email: companies.find((c: any) => c.id === formData.company_id)?.email || undefined,
                  },
                  delivery_phone: formData.delivery_phone,
                  delivery_address: (() => {
                    const fallbackAddress = order?.delivery_address || order?.delivery_address_obj;

                    if (!formData.delivery_address_obj) {
                      if (!fallbackAddress || typeof fallbackAddress !== 'object') return undefined;

                      return {
                        address_line: fallbackAddress.address_line || fallbackAddress.full_address,
                        ward: fallbackAddress.ward_name || fallbackAddress.ward?.name || fallbackAddress.ward,
                        province: fallbackAddress.province_name || fallbackAddress.province?.name || fallbackAddress.province,
                        free_address: fallbackAddress.free_address,
                        full_address: fallbackAddress.full_address,
                      };
                    }

                    if (typeof formData.delivery_address_obj === 'object') {
                      const address = formData.delivery_address_obj as any;
                      return {
                        address_line: address.address_line || address.full_address,
                        ward: address.ward_name || address.ward?.name || address.ward,
                        province: address.province_name || address.province?.name || address.province,
                        free_address: address.free_address,
                        full_address: address.full_address,
                      };
                    }
                    
                    const selectedAddress = safeAddresses.find((addr: any) => addr.id === formData.delivery_address_obj);
                    const addressSource = selectedAddress || (
                      fallbackAddress && typeof fallbackAddress === 'object'
                        ? fallbackAddress
                        : null
                    );
                    if (!addressSource) return undefined;
                    
                    return {
                      address_line: addressSource.address_line || addressSource.full_address,
                      ward: addressSource.ward_name || addressSource.ward?.name || addressSource.ward,
                      province: addressSource.province_name || addressSource.province?.name || addressSource.province,
                      free_address: addressSource.free_address,
                      full_address: addressSource.full_address,
                    };
                  })(),
                  notes: formData.notes,
                  items: orderItems.map(item => ({
                    product_name: item.product_name,
                    color: item.color,
                    quantity: item.quantity,
                    unit_name: item.unit_name,
                    unit_price: item.unit_price,
                    total_price: item.total_price,
                    isGroup: item.isGroup,
                    groupName: item.groupName,
                  })),
                  transactions: orderTransactions,
                  total_amount: totalAmount,
                  survey_fee_amount: formData.survey_fee_amount || 0,
                  shipping_installation_fee_amount: formData.shipping_installation_fee_amount || 0,
                  additional_fees: additionalFees.filter(fee => formData.additional_fee_ids.includes(fee.id)),
                  grand_total: grandTotal,
                }}
              />
            </div>
          </Box>
        )}
      </Box>
      </LocalizationProvider>
    </OrderStatusProvider>
  );
});

export default NewOrder;
